import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getRolePathFromUser } from "../../utils/rolePath";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  TablePagination,
  Tooltip,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { getInstitutePromotionLog } from "../../services/studentPromotionService";
import classService from "../../services/classService";

function formatDt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

/** Reject date strings whose year part is not exactly 4 digits (e.g. 6-digit years). */
function sanitizeDateYear(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (!s) return "";
  const yearStr = (s.split("-")[0] || "").trim();
  if (!/^\d{4}$/.test(yearStr)) return "";
  const year = parseInt(yearStr, 10);
  if (year < 1000 || year > 9999) return "";
  return s;
}

/**
 * @param {object} props
 * @param {Array<{id:number,displayName?:string,name?:string,section?:string}>} [props.classOptions]
 * @param {number} [props.refreshKey] increment to reload
 */
export default function PromotionHistoryLogPanel({ classOptions = [], refreshKey = 0 }) {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const rolePath = getRolePathFromUser(user);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [internalClassOptions, setInternalClassOptions] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classId, setClassId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const effectiveClassOptions =
    Array.isArray(classOptions) && classOptions.length > 0 ? classOptions : internalClassOptions;

  const dateRangeInvalid =
    Boolean(fromDate && toDate && toDate < fromDate);

  useEffect(() => {
    if (Array.isArray(classOptions) && classOptions.length > 0) return undefined;

    let mounted = true;
    const loadClasses = async () => {
      setClassesLoading(true);
      try {
        const instituteId = user?.instituteId;
        const resp = await classService.getAllClasses({
          page: 1,
          limit: 500,
          sort: "name",
          order: "ASC",
          ...(instituteId ? { instituteId } : {}),
        });
        const items =
          resp?.data?.classes ??
          resp?.classes ??
          resp?.data?.data?.classes ??
          (Array.isArray(resp?.data) ? resp.data : []) ??
          [];
        if (!mounted) return;
        const mapped = (Array.isArray(items) ? items : []).map((c) => {
          const className = c?.name || c?.className || "Unnamed Class";
          const division = c?.section || c?.classSection || c?.division || "";
          return {
            id: c.id || c._id,
            name: c.name,
            section: c.section,
            displayName: division ? `${className} - ${division}` : className,
          };
        });
        setInternalClassOptions(mapped);
      } catch {
        if (mounted) setInternalClassOptions([]);
      } finally {
        if (mounted) setClassesLoading(false);
      }
    };

    loadClasses();
    return () => {
      mounted = false;
    };
  }, [classOptions, user?.instituteId]);

  const load = useCallback(async () => {
    if (fromDate && toDate && toDate < fromDate) return;
    setLoading(true);
    try {
      const res = await getInstitutePromotionLog({
        classId: classId || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      const data = res?.data?.data;
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(Number(data?.count) || 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [classId, fromDate, toDate, page, rowsPerPage]);

  useEffect(() => {
    if (dateRangeInvalid) return;
    load();
  }, [load, refreshKey, dateRangeInvalid]);

  const handleChangePage = (_e, next) => {
    setPage(next);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        Each promotion stores a snapshot (previous class, new class, session, status) for audit. Students see their
        current placement only; staff can review the log here.
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, alignItems: "flex-end" }}>
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={classesLoading}>
          <InputLabel>Class (optional)</InputLabel>
          <Select
            label="Class (optional)"
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All classes</MenuItem>
            {effectiveClassOptions.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.displayName || `${c.name || ""}${c.section ? ` - ${c.section}` : ""}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="From date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(e) => {
            const nextFrom = sanitizeDateYear(e.target.value);
            setFromDate(nextFrom);
            if (toDate && nextFrom && toDate < nextFrom) {
              setToDate("");
            }
            setPage(0);
          }}
          inputProps={toDate ? { max: toDate } : undefined}
          sx={{ width: 160 }}
        />
        <TextField
          size="small"
          label="To date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(e) => {
            setToDate(sanitizeDateYear(e.target.value));
            setPage(0);
          }}
          error={dateRangeInvalid}
          helperText={dateRangeInvalid ? "To date must be on or after from date" : ""}
          inputProps={fromDate ? { min: fromDate } : undefined}
          sx={{ width: 180 }}
        />
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
          onClick={() => load()}
          disabled={loading || dateRangeInvalid}
        >
          Refresh
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Student</TableCell>
            <TableCell>Login ID</TableCell>
            <TableCell>From</TableCell>
            <TableCell>To</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Recorded by</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <CircularProgress size={28} sx={{ my: 2 }} />
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <Typography color="text.secondary" variant="body2">
                  No promotion records match your filters.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r) => (
              <TableRow key={r.id || r.uuid}>
                <TableCell>{formatDt(r.promotedAt)}</TableCell>
                <TableCell>{r.studentName || "—"}</TableCell>
                <TableCell>{r.studentLoginID ?? "—"}</TableCell>
                <TableCell>{r.fromClassLabel || "—"}</TableCell>
                <TableCell>{r.toClassLabel || "—"}</TableCell>
                <TableCell>{r.type || "—"}</TableCell>
                <TableCell>{r.promotedByName || "—"}</TableCell>
                <TableCell sx={{ maxWidth: 220 }}>
                  {r.reason ? (
                    <Tooltip title={r.reason}>
                      <Typography variant="body2" noWrap>
                        {r.reason}
                      </Typography>
                    </Tooltip>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`${rolePath}/promotion-history/${r.id}`)}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Box>
  );
}
